import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Scale, Clock, AlertTriangle, Video, MapPin, CreditCard, Banknote, CheckCircle, User, CalendarDays, CreditCard as PaymentIcon } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import api from '../utils/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Progress Bar Component
const ProgressBar = ({ currentStep, steps }) => {
  return (
    <div className="w-full mb-8">
      <div className="flex justify-between items-center">
        {steps.map((step, index) => (
          <div key={index} className="flex flex-col items-center flex-1">
            <div className={`
              w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold
              transition-all duration-300
              ${index + 1 < currentStep 
                ? 'bg-green-500 text-white' 
                : index + 1 === currentStep 
                  ? 'bg-secondary text-primary ring-4 ring-secondary/30' 
                  : 'bg-muted text-muted-foreground'}
            `}>
              {index + 1 < currentStep ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <step.icon className="w-5 h-5" />
              )}
            </div>
            <span className={`
              mt-2 text-xs font-medium text-center hidden sm:block
              ${index + 1 === currentStep ? 'text-primary' : 'text-muted-foreground'}
            `}>
              {step.label}
            </span>
          </div>
        ))}
      </div>
      <div className="relative mt-2">
        <div className="absolute top-0 left-0 h-1 bg-muted rounded-full w-full" />
        <div 
          className="absolute top-0 left-0 h-1 bg-secondary rounded-full transition-all duration-500"
          style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
        />
      </div>
    </div>
  );
};

// Order Summary Component
const OrderSummary = ({ formData, selectedSlot, price, onConfirm, onBack, loading }) => {
  const consultationType = formData.consultationType === 'video' ? 'Videochiamata' : 'In Presenza';
  const paymentMethod = formData.paymentMethod === 'cash' ? 'Contanti in studio' : 'Pagamento online';
  
  return (
    <Card className="p-6 bg-gradient-to-br from-primary/5 to-secondary/5 border-2 border-secondary/20">
      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
        <CheckCircle className="w-5 h-5 text-secondary" />
        Riepilogo Prenotazione
      </h3>
      
      <div className="space-y-3">
        <div className="flex justify-between py-2 border-b">
          <span className="text-muted-foreground">Cliente</span>
          <span className="font-medium">{formData.fullName}</span>
        </div>
        <div className="flex justify-between py-2 border-b">
          <span className="text-muted-foreground">Email</span>
          <span className="font-medium">{formData.email}</span>
        </div>
        <div className="flex justify-between py-2 border-b">
          <span className="text-muted-foreground">Telefono</span>
          <span className="font-medium">{formData.phone}</span>
        </div>
        <div className="flex justify-between py-2 border-b">
          <span className="text-muted-foreground">Data</span>
          <span className="font-medium">{selectedSlot?.date}</span>
        </div>
        <div className="flex justify-between py-2 border-b">
          <span className="text-muted-foreground">Orario</span>
          <span className="font-medium">{selectedSlot?.start_time} - {selectedSlot?.end_time}</span>
        </div>
        <div className="flex justify-between py-2 border-b">
          <span className="text-muted-foreground">Tipo Consulenza</span>
          <span className="font-medium">{consultationType}</span>
        </div>
        <div className="flex justify-between py-2 border-b">
          <span className="text-muted-foreground">Pagamento</span>
          <span className="font-medium">{paymentMethod}</span>
        </div>
        <div className="flex justify-between py-3 bg-secondary/10 rounded-lg px-3 mt-4">
          <span className="text-lg font-bold">Totale</span>
          <span className="text-2xl font-bold text-secondary">€{price.toFixed(2)}</span>
        </div>
      </div>
      
      <div className="flex gap-3 mt-6">
        <Button variant="outline" onClick={onBack} className="flex-1">
          Modifica
        </Button>
        <Button 
          onClick={onConfirm} 
          disabled={loading}
          className="flex-1 bg-secondary text-primary hover:bg-secondary/90"
        >
          {loading ? 'Conferma in corso...' : 'Conferma e Procedi'}
        </Button>
      </div>
    </Card>
  );
};

const BookingPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  // Step management
  const [currentStep, setCurrentStep] = useState(1); // 1: form, 2: slot selection, 3: payment
  
  // Pre-booking form state
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    confirmEmail: '',
    nationality: '',
    consultationReason: '',
    consultationType: '', // 'video' or 'presence'
    paymentMethod: '', // 'immediate' or 'cash' (only for presence)
    privacyConsent: false,
    dataProcessingConsent: false
  });
  const [formErrors, setFormErrors] = useState({});
  
  // Existing state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [appointmentId, setAppointmentId] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [caseDescription, setCaseDescription] = useState('');
  const [price, setPrice] = useState(60);
  const [isCashPayment, setIsCashPayment] = useState(false);
  const isRTL = language === 'ar' || language === 'ur';

  // Calculate price based on consultation type and payment method
  const calculatePrice = () => {
    return 60;
  };

  // Fetch slots function - memoized with useCallback (must be defined before useEffect)
  const fetchSlots = useCallback(async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await api.get(`/slots/available?date=${dateStr}`);
      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
      setAvailableSlots([]);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (currentStep === 2) {
      fetchSlots();
    }
  }, [selectedDate, currentStep, fetchSlots]);

  const fetchPrice = async () => {
    try {
      const response = await api.get('/settings/price');
      // Price is now calculated based on consultation type
    } catch (error) {
      console.error('Error fetching price:', error);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.fullName.trim()) {
      errors.fullName = 'Nome e cognome sono obbligatori';
    }
    
    if (!formData.phone.trim()) {
      errors.phone = 'Numero di cellulare obbligatorio';
    } else if (!/^[0-9+\s]{8,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      errors.phone = 'Numero di cellulare non valido';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email obbligatoria';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Email non valida';
    }
    
    if (!formData.confirmEmail.trim()) {
      errors.confirmEmail = 'Conferma email obbligatoria';
    } else if (formData.email !== formData.confirmEmail) {
      errors.confirmEmail = 'Le email non corrispondono';
    }
    
    if (!formData.nationality.trim()) {
      errors.nationality = 'Nazionalità obbligatoria';
    }
    
    if (!formData.consultationReason.trim()) {
      errors.consultationReason = 'Descrivi brevemente il motivo della consulenza';
    }
    
    if (!formData.consultationType) {
      errors.consultationType = 'Seleziona il tipo di consulenza';
    }
    
    if (!formData.privacyConsent) {
      errors.privacyConsent = 'Devi accettare la Privacy Policy';
    }
    
    if (!formData.dataProcessingConsent) {
      errors.dataProcessingConsent = 'Devi dare il consenso al trattamento dei dati';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = () => {
    if (validateForm()) {
      const calculatedPrice = calculatePrice();
      setPrice(calculatedPrice);
      setIsCashPayment(false);
      setCurrentStep(2);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Countdown timer effect
  useEffect(() => {
    if (!expiresAt) return;
    
    const interval = setInterval(() => {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diff = expiry - now;
      
      if (diff <= 0) {
        setTimeLeft(null);
        setShowPayment(false);
        setAppointmentId(null);
        setExpiresAt(null);
        toast.error('Tempo scaduto! La prenotazione è stata annullata.');
        fetchSlots(); // Refresh slots
        clearInterval(interval);
      } else {
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [expiresAt, fetchSlots]);

  const handleBookSlot = async (slotId) => {
    setLoading(true);
    try {
      // Use guest booking endpoint (no authentication required)
      const response = await api.post('/appointments/book-guest', { 
        slot_id: slotId,
        full_name: formData.fullName,
        phone: formData.phone,
        email: formData.email,
        nationality: formData.nationality,
        consultation_reason: formData.consultationReason,
        consultation_type: formData.consultationType,
        payment_method: formData.consultationType === 'video' ? 'video' : formData.paymentMethod,
        case_description: formData.consultationReason,  // Use the consultation reason from form
        payment_amount: price
      });
      
      // For cash payment, booking is complete
      if (response.data.payment_method === 'cash') {
        toast.success('Prenotazione confermata! Riceverai una email di conferma.');
        navigate('/');
        return;
      }
      
      setAppointmentId(response.data.appointment_id);
      setExpiresAt(response.data.expires_at);
      toast.success('Slot riservato! Completa il pagamento entro 15 minuti.');
      setShowPayment(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore prenotazione');
    } finally {
      setLoading(false);
    }
  };

  const createPayPalOrder = async () => {
    try {
      // SECURITY: Price is now determined server-side, not sent from client
      const response = await api.post('/payments/create-order-guest', {
        appointment_id: appointmentId,
        guest_email: formData.email
      });
      return response.data.order_id;
    } catch (error) {
      toast.error('Errore creazione ordine');
      throw error;
    }
  };

  const onPayPalApprove = async (data) => {
    try {
      await api.post('/payments/capture-order-guest', {
        order_id: data.orderID,
        appointment_id: appointmentId,
        guest_email: formData.email
      });
      toast.success('Pagamento completato! Riceverai una email di conferma.');
      navigate('/');
    } catch (error) {
      toast.error('Errore nel pagamento');
    }
  };

  return (
    <PayPalScriptProvider options={{ 
      "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID || 'test',
      currency: 'EUR',
      intent: 'capture',
      components: 'buttons',
      "enable-funding": 'paylater'
    }}>
      <div className={`min-h-screen bg-muted ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <nav className="bg-primary text-white border-b border-secondary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_legal-booking-hub/artifacts/evfhhmo1_logo%20studio%20legale_edited_edited.avif" 
                  alt="Studio Legale"
                  className="h-14 w-14 object-contain"
                  style={{ imageRendering: 'crisp-edges' }}
                />
                <span className="text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Studio Legale
                </span>
              </div>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/dashboard')}
                className="text-white hover:bg-white/10"
              >
                {t('nav.dashboard')}
              </Button>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('booking.title')}
          </h1>

          {/* Progress Bar */}
          <ProgressBar 
            currentStep={currentStep}
            steps={[
              { label: 'Dati Personali', icon: User },
              { label: 'Scegli Orario', icon: CalendarDays },
              { label: 'Pagamento', icon: PaymentIcon }
            ]}
          />

          {/* Step 1: Pre-booking Form */}
          {currentStep === 1 && (
            <Card className="p-8 max-w-2xl mx-auto">
              <h2 className="text-2xl font-semibold mb-6 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
                Informazioni per la Consulenza
              </h2>
              
              <div className="space-y-6">
                {/* Full Name */}
                <div>
                  <Label htmlFor="fullName" className="text-base font-medium">
                    Nome e Cognome Completo *
                  </Label>
                  <Input
                    id="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    placeholder="Es. Mario Rossi"
                    className={`mt-2 ${formErrors.fullName ? 'border-destructive' : ''}`}
                    data-testid="input-fullname"
                  />
                  {formErrors.fullName && (
                    <p className="text-sm text-destructive mt-1">{formErrors.fullName}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <Label htmlFor="phone" className="text-base font-medium">
                    Numero di Cellulare *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Es. +39 333 1234567"
                    className={`mt-2 ${formErrors.phone ? 'border-destructive' : ''}`}
                    data-testid="input-phone"
                  />
                  {formErrors.phone && (
                    <p className="text-sm text-destructive mt-1">{formErrors.phone}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-base font-medium">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Es. mario.rossi@email.com"
                    className={`mt-2 ${formErrors.email ? 'border-destructive' : ''}`}
                    data-testid="input-email"
                  />
                  {formErrors.email && (
                    <p className="text-sm text-destructive mt-1">{formErrors.email}</p>
                  )}
                </div>

                {/* Confirm Email */}
                <div>
                  <Label htmlFor="confirmEmail" className="text-base font-medium">
                    Conferma Email *
                  </Label>
                  <Input
                    id="confirmEmail"
                    type="email"
                    value={formData.confirmEmail}
                    onChange={(e) => handleInputChange('confirmEmail', e.target.value)}
                    placeholder="Ripeti la tua email"
                    className={`mt-2 ${formErrors.confirmEmail ? 'border-destructive' : ''}`}
                    data-testid="input-confirm-email"
                  />
                  {formErrors.confirmEmail && (
                    <p className="text-sm text-destructive mt-1">{formErrors.confirmEmail}</p>
                  )}
                </div>

                {/* Nationality */}
                <div>
                  <Label htmlFor="nationality" className="text-base font-medium">
                    Nazionalità *
                  </Label>
                  <Input
                    id="nationality"
                    type="text"
                    value={formData.nationality}
                    onChange={(e) => handleInputChange('nationality', e.target.value)}
                    placeholder="Es. Italiana, Albanese, Rumena..."
                    className={`mt-2 ${formErrors.nationality ? 'border-destructive' : ''}`}
                    data-testid="input-nationality"
                  />
                  {formErrors.nationality && (
                    <p className="text-sm text-destructive mt-1">{formErrors.nationality}</p>
                  )}
                </div>

                {/* Consultation Reason */}
                <div>
                  <Label htmlFor="consultationReason" className="text-base font-medium">
                    Motivo della Consulenza (in breve) *
                  </Label>
                  <Textarea
                    id="consultationReason"
                    value={formData.consultationReason}
                    onChange={(e) => handleInputChange('consultationReason', e.target.value)}
                    placeholder="Descrivi brevemente il motivo per cui richiedi la consulenza legale..."
                    className={`mt-2 min-h-[100px] ${formErrors.consultationReason ? 'border-destructive' : ''}`}
                    data-testid="input-consultation-reason"
                  />
                  {formErrors.consultationReason && (
                    <p className="text-sm text-destructive mt-1">{formErrors.consultationReason}</p>
                  )}
                </div>

                {/* Consultation Type */}
                <div>
                  <Label className="text-base font-medium mb-3 block">
                    Tipo di Consulenza *
                  </Label>
                  <div className="space-y-3">
                    {/* Video Call Option */}
                    <div
                      onClick={() => {
                        handleInputChange('consultationType', 'video');
                        handleInputChange('paymentMethod', '');
                      }}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.consultationType === 'video'
                          ? 'border-secondary bg-secondary/10'
                          : 'border-muted hover:border-secondary/50'
                      }`}
                      data-testid="option-video"
                    >
                      <div className="flex items-center gap-3">
                        <Video className={`h-6 w-6 ${formData.consultationType === 'video' ? 'text-secondary' : 'text-muted-foreground'}`} />
                        <div className="flex-1">
                          <p className="font-semibold">Consulenza in Videochiamata</p>
                          <p className="text-sm text-muted-foreground">Comodamente da casa tua</p>
                        </div>
                        <span className="text-xl font-bold text-primary">€60</span>
                      </div>
                    </div>

                    {/* In Person Option */}
                    <div
                      onClick={() => handleInputChange('consultationType', 'presence')}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.consultationType === 'presence'
                          ? 'border-secondary bg-secondary/10'
                          : 'border-muted hover:border-secondary/50'
                      }`}
                      data-testid="option-presence"
                    >
                      <div className="flex items-center gap-3">
                        <MapPin className={`h-6 w-6 ${formData.consultationType === 'presence' ? 'text-secondary' : 'text-muted-foreground'}`} />
                        <div className="flex-1">
                          <p className="font-semibold">Consulenza in Presenza</p>
                          <p className="text-sm text-muted-foreground">Presso lo studio di Casale Monferrato</p>
                        </div>
                        <span className="text-xl font-bold text-primary">€60</span>
                      </div>
                    </div>
                  </div>
                  {formErrors.consultationType && (
                    <p className="text-sm text-destructive mt-2">{formErrors.consultationType}</p>
                  )}
                </div>

                {/* Privacy Consents */}
                <div className="pt-4 border-t space-y-4">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="privacyConsent"
                      checked={formData.privacyConsent}
                      onCheckedChange={(checked) => handleInputChange('privacyConsent', checked)}
                      className="mt-1"
                      data-testid="checkbox-privacy"
                    />
                    <div>
                      <Label htmlFor="privacyConsent" className="text-sm cursor-pointer">
                        Ho preso visione della <a href="/privacy" className="text-secondary underline" target="_blank">Privacy Policy</a> e do il consenso al trattamento dei dati. *
                      </Label>
                      {formErrors.privacyConsent && (
                        <p className="text-sm text-destructive mt-1">{formErrors.privacyConsent}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="dataProcessingConsent"
                      checked={formData.dataProcessingConsent}
                      onCheckedChange={(checked) => handleInputChange('dataProcessingConsent', checked)}
                      className="mt-1"
                      data-testid="checkbox-data-processing"
                    />
                    <div>
                      <Label htmlFor="dataProcessingConsent" className="text-sm cursor-pointer">
                        Consenso al trattamento dei dati personali ai sensi del GDPR (Obbligatorio) *
                      </Label>
                      {formErrors.dataProcessingConsent && (
                        <p className="text-sm text-destructive mt-1">{formErrors.dataProcessingConsent}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Legal Disclaimer */}
                <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
                  <p className="text-sm text-destructive leading-relaxed">
                    <span className="font-bold text-lg">*</span> Il costo della consulenza verrà detratto dalla parcella nel caso in cui l'avvocato riceverà il mandato dal cliente; diversamente, il pagamento della consulenza sarà trattenuto in quanto compenso per l'attività svolta dall'avvocato.
                  </p>
                </div>

                {/* Submit Button */}
                <Button
                  onClick={handleFormSubmit}
                  className="w-full bg-secondary text-primary hover:bg-secondary/90 py-6 text-lg"
                  data-testid="btn-continue"
                >
                  Continua - Seleziona Data e Ora
                </Button>
              </div>
            </Card>
          )}

          {/* Step 2: Slot Selection */}
          {currentStep === 2 && !showPayment && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="p-6 lg:col-span-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h2 className="text-xl font-semibold mb-4">{t('booking.selectDate')}</h2>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      className="rounded-sm border"
                      data-testid="booking-calendar"
                    />
                  </div>

                  <div>
                    <h2 className="text-xl font-semibold mb-4">{t('booking.selectTime')}</h2>
                    <p className="text-sm text-muted-foreground mb-4">
                      Data selezionata: <span className="font-semibold text-primary">{format(selectedDate, 'dd/MM/yyyy')}</span>
                    </p>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {availableSlots.length === 0 ? (
                        <div className="text-center py-8 bg-muted/30 rounded-lg">
                          <Clock className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
                          <p className="text-muted-foreground font-medium">
                            Nessuno slot disponibile
                          </p>
                          <p className="text-sm text-muted-foreground mt-2">
                            Seleziona un'altra data
                          </p>
                        </div>
                      ) : (
                        availableSlots.map((slot) => (
                          <Button
                            key={slot.id}
                            onClick={() => {
                              setSelectedSlot(slot);
                              handleBookSlot(slot.id);
                            }}
                            disabled={loading}
                            className="w-full justify-between bg-white text-primary border-2 border-secondary hover:bg-secondary/10 disabled:opacity-50"
                            data-testid={`slot-${slot.id}`}
                          >
                            <span className="font-medium">{slot.start_time} - {slot.end_time}</span>
                            <span className="text-success font-medium">{t('booking.available')}</span>
                          </Button>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Back Button */}
                <div className="mt-6 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    onClick={() => setCurrentStep(1)}
                    className="w-full sm:w-auto"
                  >
                    ← Torna ai Dati Personali
                  </Button>
                </div>
              </Card>

              {/* Mini Summary Sidebar */}
              <Card className="p-6 bg-primary/5 h-fit">
                <h3 className="font-semibold mb-4">Il tuo preventivo</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cliente</span>
                    <span className="font-medium">{formData.fullName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tipo</span>
                    <span className="font-medium">
                      {formData.consultationType === 'video' ? 'Videochiamata' : 'In Presenza'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Pagamento</span>
                    <span className="font-medium">
                      {formData.paymentMethod === 'cash' ? 'Contanti' : 'Online'}
                    </span>
                  </div>
                  <div className="pt-3 mt-3 border-t flex justify-between">
                    <span className="font-bold">Totale</span>
                    <span className="font-bold text-secondary text-lg">€{price.toFixed(2)}</span>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Step 3: Payment */}
          {currentStep === 2 && showPayment && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {/* Order Summary */}
              <OrderSummary
                formData={formData}
                selectedSlot={selectedSlot}
                price={price}
                onBack={() => {
                  setShowPayment(false);
                  setAppointmentId(null);
                  setExpiresAt(null);
                }}
                onConfirm={() => {}} // PayPal handles payment
                loading={loading}
              />

              {/* Payment Section */}
              <Card className="p-6">
                {/* Timer Warning */}
                {timeLeft && (
                  <div className={`mb-6 p-4 rounded-lg flex items-center justify-center gap-3 ${
                    parseInt(timeLeft.split(':')[0]) < 5 
                      ? 'bg-destructive/10 border-2 border-destructive animate-pulse' 
                      : 'bg-warning/10 border border-warning'
                  }`}>
                    <Clock className={`h-8 w-8 ${parseInt(timeLeft.split(':')[0]) < 5 ? 'text-destructive' : 'text-warning'}`} />
                    <div className="text-center">
                      <p className="text-xs font-medium uppercase tracking-wide">Tempo rimanente</p>
                      <p className={`text-3xl font-bold ${parseInt(timeLeft.split(':')[0]) < 5 ? 'text-destructive' : 'text-warning'}`}>
                        {timeLeft}
                      </p>
                    </div>
                  </div>
                )}
                
                <h2 className="text-xl font-semibold mb-4 text-center">
                  Completa il Pagamento
                </h2>
                
                {/* Warning message */}
                <div className="mb-4 p-3 bg-muted/50 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-warning mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    Lo slot è riservato per 15 minuti. Se non completi il pagamento, la prenotazione verrà annullata automaticamente.
                  </p>
                </div>
              
              {/* Legal disclaimer about consultation cost */}
              <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <p className="text-sm text-foreground leading-relaxed">
                  <strong className="text-primary">Nota importante:</strong> Il costo della consulenza verrà detratto dalla parcella nel caso in cui l'avvocato riceverà il mandato dal cliente; diversamente, il pagamento della consulenza sarà trattenuto in quanto compenso per l'attività svolta dall'avvocato.
                </p>
              </div>
              
              {/* Consultation Type Info */}
              <div className="mb-4 p-3 bg-secondary/10 rounded-lg">
                <p className="text-sm text-center">
                  <strong>Tipo consulenza:</strong> {formData.consultationType === 'video' ? 'Videochiamata' : 'In presenza a Casale Monferrato'}
                </p>
              </div>
              
              {/* Cash Payment - No PayPal needed */}
              {isCashPayment ? (
                <div className="text-center">
                  <div className="mb-6 p-6 bg-success/10 border border-success rounded-lg">
                    <Banknote className="h-12 w-12 mx-auto mb-3 text-success" />
                    <h3 className="text-xl font-bold text-success mb-2">Pagamento in Contanti</h3>
                    <p className="text-muted-foreground">
                      Il pagamento di <strong>€{price.toFixed(2)}</strong> avverrà direttamente in studio al momento della consulenza.
                    </p>
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        // Confirm booking without payment
                        toast.success('Prenotazione confermata! Pagherai in contanti in studio.');
                        navigate('/dashboard');
                      } catch (error) {
                        toast.error('Errore nella conferma');
                      }
                    }}
                    className="w-full bg-success text-white hover:bg-success/90 py-6 text-lg"
                  >
                    Conferma Prenotazione
                  </Button>
                </div>
              ) : (
                <>
                  <PayPalButtons
                    createOrder={createPayPalOrder}
                    onApprove={onPayPalApprove}
                    style={{ layout: 'vertical' }}
                    fundingSource={undefined}
                  />
                  
                  {/* Pay Later info */}
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800 text-center">
                      <strong>Paga in 3 rate</strong> - Dividi il pagamento in 3 rate senza interessi con PayPal
                    </p>
                  </div>
                </>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  setShowPayment(false);
                  setAppointmentId(null);
                  setExpiresAt(null);
                  setTimeLeft(null);
                  fetchSlots();
                }}
                className="w-full mt-4"
              >
                Annulla
              </Button>
              </Card>
            </div>
          )}
        </div>
      </div>
    </PayPalScriptProvider>
  );
};

export default BookingPage;
